import React from 'react'
import GenericErrorTurnback from './GenericErrorTurnback'
import { logError } from '../../services/error/logger'

interface IErrorBoundaryProps {
  location: {
    pathname: string
  }
  children: React.ReactNode
}

interface IErrorBoundaryState {
  hasError: boolean
}

class ErrorBoundary extends React.Component<IErrorBoundaryProps, IErrorBoundaryState> {
  constructor(props: IErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  public static getDerivedStateFromError() {
    return { hasError: true }
  }

  public componentDidUpdate(prevProps: Readonly<IErrorBoundaryProps>): void {
    const { location } = this.props
    if (location.pathname !== prevProps.location.pathname) {
      this.setState({ hasError: false })
    }
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // TODO - investigate errorInfo
    logError(error)
    // eslint-disable-next-line no-console
    console.error('Caught an error:', error, errorInfo)
  }

  public render() {
    if (this.state.hasError) {
      return (
        <GenericErrorTurnback
          mangoTurnBack={{
            testId: 'system-error',
            title: 'System Error',
            variant: 'generic-error',
            description: [
              <p key="1">Something went wrong on our end</p>,
              <p key="2">Try the following to resolve the issue</p>,
              <ul key="3">
                <li>Refresh the page and try again</li>
                <li>Contact our customer support</li>
              </ul>,
            ],
          }}
          mangoButton={{
            key: 'refresh-button',
            variant: 'primary',
            testId: 'refresh-button',
            actionType: 'refresh',
            buttonText: 'Refresh',
          }}
          mangoButtonLink={{
            variant: 'tertiary',
            key: 'help-centre-button',
            testId: 'help-centre-button',
            href: 'https://help.humanforce.com/hc/en-au/articles/360029504211-Welcome-to-the-Humanforce-Help-Portal',
            target: '_blank',
            text: 'Help Centre',
          }}
        />
      )
    }

    return this.props.children
  }
}

export { ErrorBoundary }
